import React, { useState, useMemo, useEffect } from 'react';
import { useTerminology } from '../hooks/useTerminology';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useFormatting } from '@/hooks/useFormatting';
import { usePermissions } from '@/hooks/usePermissions';
import { useActions } from '@/contexts/ActionsContext';
import { useApp } from '@/contexts/AppContext';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { useRecommendations, type ReviewedItem, type InsightComment } from '@/contexts/RecommendationsContext';
import { UserAvatar } from '@/components/ui/user-avatar';
import { PermissionGate } from '@/components/permissions/PermissionGate';
import { CreateActionModal } from '@/components/actions/CreateActionModal';
import {
  allRecommendations,
  aiSummaryMetrics,
  aiAlertSummary,
  aiImpactVisualization,
  type Recommendation,
  type RecommendationCategory,
  type RecommendationPriority,
  type RecurringTransactionSuggestion,
} from '../data/aiRecommendationsData';
import type { RecurringTransaction } from '../types/recurring-transaction';
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Sparkles,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Filter,
  CheckCircle2,
  RotateCcw,
  Brain,
  Zap,
  ShieldAlert,
  PlusCircle,
  Clock,
  MessageSquare,
  Send,
  Info,
  Lock,
  RepeatIcon,
} from 'lucide-react';
import { ImpactHistoryIcon } from '@/components/scenarios/ImpactHistoryIcon';

// ── Types ─────────────────────────────────────────────────────────────────────

type PriorityFilter = 'ALL' | RecommendationPriority;
type CategoryFilter = 'ALL' | RecommendationCategory;

// ── Helpers ───────────────────────────────────────────────────────────────────

const priorityColour = (priority: RecommendationPriority) => {
  switch (priority) {
    case 'HIGH':   return 'bg-red-100 text-red-800 border-red-200';
    case 'MEDIUM': return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'LOW':    return 'bg-blue-100 text-blue-800 border-blue-200';
  }
};

const categoryIcon = (category: RecommendationCategory) => {
  switch (category) {
    case 'STRATEGIC':   return <Brain className="h-4 w-4 text-purple-600" />;
    case 'OPERATIONAL': return <Zap className="h-4 w-4 text-blue-600" />;
  }
};

const categoryBadgeColour = (category: RecommendationCategory) => {
  switch (category) {
    case 'STRATEGIC':   return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'OPERATIONAL': return 'bg-blue-100 text-blue-800 border-blue-200';
  }
};

const formatReviewedTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1)    return 'just now';
  if (diffMins < 60)   return `${diffMins}m ago`;
  if (diffHours < 24)  return `${diffHours}h ago`;
  if (diffDays === 1)  return 'yesterday';
  return `${diffDays} days ago`;
};

// ── Generate Recurring Transaction Modal ─────────────────────────────────────

interface GenerateRecurringModalProps {
  suggestion: RecurringTransactionSuggestion;
  onConfirm: (entry: RecurringTransaction) => void;
  onClose: () => void;
}

const GenerateRecurringModal: React.FC<GenerateRecurringModalProps> = ({ suggestion, onConfirm, onClose }) => {
  const { getCashMovementTerms } = useTerminology();
  const cashTerms = getCashMovementTerms();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [name, setName]                   = useState(suggestion.name);
  const [amount, setAmount]               = useState(suggestion.amount.toString());
  const [direction, setDirection]         = useState<'inflow' | 'outflow'>(suggestion.direction);
  const [frequency, setFrequency]         = useState(suggestion.frequency);
  const [category, setCategory]           = useState(suggestion.category);
  const [counterpartyName, setCounterpartyName] = useState(suggestion.counterpartyName ?? '');
  const [notes, setNotes]                 = useState(suggestion.notes ?? '');
  const [startDate, setStartDate]         = useState(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  });

  const handleConfirm = () => {
    const start = new Date(startDate);
    const entry: RecurringTransaction = {
      id: `AI-${Date.now()}`,
      name,
      direction,
      amount: parseFloat(amount) || 0,
      isVariable: false,
      frequency: frequency as RecurringTransaction['frequency'],
      startDate: start,
      nextDueDate: start,
      category,
      counterpartyName: counterpartyName || undefined,
      status: 'Active',
      notes: notes || undefined,
      dataSourceName: 'Manual Entry - AI Recommendations',
      provenance: 'ai-suggested',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    onConfirm(entry);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RepeatIcon className="h-5 w-5 text-purple-600" />
            Generate Recurring Transaction
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Review and adjust the AI-suggested values before saving. All fields are editable.
          </p>
        </DialogHeader>

        {/* AI provenance callout */}
        <div className="flex items-start gap-2 rounded-lg bg-purple-50 border border-purple-200 px-4 py-3 text-sm text-purple-900">
          <Brain className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
          <span>This entry was generated from an AI insight. Once saved it will appear in Recurring Transactions with an <strong>AI-suggested</strong> badge. Editing and saving will transition it to <strong>Manually confirmed</strong>.</span>
        </div>

        <div className="grid gap-4 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="rt-name">Name</Label>
            <Input id="rt-name" value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="rt-direction">Direction</Label>
              <Select value={direction} onValueChange={v => setDirection(v as 'inflow' | 'outflow')}>
                <SelectTrigger id="rt-direction">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inflow">{cashTerms.in}</SelectItem>
                  <SelectItem value="outflow">{cashTerms.out}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="rt-amount">Amount (£)</Label>
              <Input id="rt-amount" type="number" value={amount} onChange={e => setAmount(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="rt-frequency">Frequency</Label>
              <Select value={frequency} onValueChange={v => setFrequency(v as RecurringTransactionSuggestion['frequency'])}>
                <SelectTrigger id="rt-frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(['Daily','Weekly','Fortnightly','Monthly','Quarterly','Annual'] as const).map(f => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="rt-start">Start Date</Label>
              <DatePicker id="rt-start" value={startDate ? new Date(startDate) : undefined} onChange={date => setStartDate(date ? date.toISOString().split('T')[0] : '')} />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="rt-category">Category</Label>
            <Input id="rt-category" value={category} onChange={e => setCategory(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="rt-counterparty">Counterparty (optional)</Label>
            <Input id="rt-counterparty" value={counterpartyName} onChange={e => setCounterpartyName(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="rt-notes">Notes</Label>
            <Textarea id="rt-notes" value={notes} onChange={e => setNotes(e.target.value)} rows={2} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={!name.trim() || !amount || parseFloat(amount) <= 0}
            className="gap-2 bg-purple-600 hover:bg-purple-700"
          >
            <RepeatIcon className="h-4 w-4" />
            Save Recurring Transaction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Impact Visualisation Modal ────────────────────────────────────────────────

const ImpactModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { formatCurrency } = useFormatting();
  const viz = aiImpactVisualization;
  const maxCash = Math.max(...viz.before.map(d => d.cash), ...viz.after.map(d => d.cash));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Cash Flow Impact Analysis</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div className="grid grid-cols-2 gap-6">
            {/* Without recommendations */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-red-600" />
                Without Acting on Insights
              </h3>
              <div className="space-y-3">
                {viz.before.map((point, idx) => (
                  <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="text-sm text-gray-500 mb-1">{point.label}</div>
                    <div className="text-2xl font-bold text-red-600">{formatCurrency(point.cash, 'full')}</div>
                    <div className="mt-2 bg-red-200 rounded-full h-2">
                      <div className="bg-red-500 h-2 rounded-full" style={{ width: `${(point.cash / maxCash) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* With recommendations */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                After Acting on All Insights
              </h3>
              <div className="space-y-3">
                {viz.after.map((point, idx) => (
                  <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="text-sm text-gray-500 mb-1">{point.label}</div>
                    <div className="text-2xl font-bold text-green-600">{formatCurrency(point.cash, 'full')}</div>
                    <div className="mt-2 bg-green-200 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: `${(point.cash / maxCash) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Key milestones */}
          {viz.annotations.length > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Key actions driving the improvement</h3>
              <ul className="space-y-2">
                {viz.annotations.map((note, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{note.note}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
                <span className="text-sm text-gray-600">Total 90-day improvement</span>
                <span className="text-lg font-bold text-green-600">+{formatCurrency(viz.improvement, 'full')}</span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Insight Discussion Modal ──────────────────────────────────────────────────
// Identical chat UI to the ActionsPage DiscussionModal — same visual pattern,
// different data source (insight comments from RecommendationsContext).

interface InsightDiscussionModalProps {
  rec: Recommendation | null;
  onClose: () => void;
}

const InsightDiscussionModal: React.FC<InsightDiscussionModalProps> = ({ rec, onClose }) => {
  const [newMessage, setNewMessage]           = useState('');
  const messagesEndRef                        = React.useRef<HTMLDivElement>(null);
  const { currentUser }                       = useApp();
  const { getAvatarUrlByName }                = useUserPreferences();
  const { addInsightComment, getInsightComments } = useRecommendations();

  const comments = rec ? getInsightComments(rec.id) : [];

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments.length]);

  if (!rec) return null;

  const handleSend = () => {
    if (!newMessage.trim()) return;
    addInsightComment(rec.id, newMessage.trim());
    setNewMessage('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTimestamp = (date: Date) => {
    const now   = new Date();
    const diff  = now.getTime() - date.getTime();
    const hours = diff / (1000 * 60 * 60);
    if (hours < 24) return `Today ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    if (hours < 48) return `Yesterday ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  return (
    <Dialog open={!!rec} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-muted-foreground" />
            Discussion
          </DialogTitle>
          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{rec.title}</p>
        </DialogHeader>

        {/* Messages */}
        <div className="flex flex-col h-80 bg-muted/30 rounded-lg border overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {comments.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                <MessageSquare className="h-8 w-8 mb-2 opacity-30" />
                <p className="text-sm font-medium">No messages yet</p>
                <p className="text-xs">Start the discussion about this insight</p>
              </div>
            ) : (
              <>
                {comments.map((comment: InsightComment) => {
                  const isMe = comment.authorId === currentUser.id;
                  return (
                    <div key={comment.id} className={`flex gap-2 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                      <UserAvatar
                        name={comment.authorName}
                        seed={comment.authorName}
                        avatarUrl={getAvatarUrlByName(comment.authorName)}
                        size="sm"
                      />
                      <div className={`flex flex-col max-w-[75%] ${isMe ? 'items-end' : 'items-start'}`}>
                        <div className={`flex items-center gap-2 mb-1 text-xs text-muted-foreground ${isMe ? 'flex-row-reverse' : ''}`}>
                          <span className="font-medium">
                            {isMe ? `You (${comment.authorName})` : comment.authorName}
                          </span>
                          <span className="text-muted-foreground/60">{formatTimestamp(comment.createdAt)}</span>
                        </div>
                        <div className={`rounded-2xl px-3 py-2 text-sm ${isMe ? 'bg-primary text-primary-foreground rounded-tr-sm' : 'bg-background border rounded-tl-sm'}`}>
                          <p className="whitespace-pre-wrap break-words">{comment.text}</p>
                        </div>
                        <p className="text-xs text-muted-foreground/60 mt-0.5">{comment.authorRole}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input */}
          <div className="border-t bg-background p-3 flex gap-2">
            <Textarea
              placeholder="Type a message… (Enter to send)"
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              className="resize-none flex-1 min-h-0"
            />
            <Button
              size="sm"
              onClick={handleSend}
              disabled={!newMessage.trim()}
              className="self-end"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Footer note — explains persistence into Reviewed tab */}
        <p className="text-xs text-muted-foreground text-center">
          Discussion is preserved if this insight is marked as reviewed
        </p>
      </DialogContent>
    </Dialog>
  );
};

// ── Recommendation card (shared between Open and Reviewed tabs) ───────────────

interface RecommendationCardProps {
  rec: Recommendation;
  isExpanded: boolean;
  onToggle: () => void;
  reviewedItem?: ReviewedItem;
  onMarkReviewed?: () => void;
  onReinstate?: () => void;
  onCreateAction?: () => void;
  onGenerateRecurring?: () => void;
  onDiscuss: () => void;
  canDismiss: boolean;
  canCreateAction: boolean;
  canManageRecurring: boolean;
  commentCount: number;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({
  rec,
  isExpanded,
  onToggle,
  reviewedItem,
  onMarkReviewed,
  onReinstate,
  onCreateAction,
  onGenerateRecurring,
  onDiscuss,
  canDismiss,
  canCreateAction,
  canManageRecurring,
  commentCount,
}) => {
  const { formatCurrency } = useFormatting();
  const isReviewed = !!reviewedItem;

  return (
    <Card className={`transition-shadow ${isReviewed ? 'opacity-75 border-gray-200' : 'hover:shadow-lg'}`}>
      {/* Card header — always visible, click to expand */}
      <CardContent
        className="pt-6 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {categoryIcon(rec.category)}
              <Badge className={categoryBadgeColour(rec.category)}>
                {rec.category}
              </Badge>
              <Badge className={priorityColour(rec.priority)}>
                {rec.priority} PRIORITY
              </Badge>
              <span className="text-sm text-gray-500">• {rec.effort} effort</span>
              <span className="text-sm text-gray-500">• {rec.deadline}</span>
              {isReviewed && reviewedItem && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Reviewed {formatReviewedTime(reviewedItem.reviewedAt)}
                </Badge>
              )}
            </div>
            <h3 className={`text-xl font-bold ${isReviewed ? 'text-gray-500' : 'text-gray-900'}`}>{rec.title}</h3>
            <p className="text-gray-600 mt-1">{rec.summary}</p>
          </div>
          <div className="flex items-center gap-3 ml-4 flex-shrink-0">
            <div className="text-right">
              <div className="text-xs text-gray-500 flex items-center justify-end gap-1">
                Potential impact
                {rec.impactHistory && rec.impactHistory.length > 0 && (
                  <ImpactHistoryIcon
                    history={rec.impactHistory}
                    title={`Impact History — ${rec.title}`}
                  />
                )}
              </div>
              <div className={`text-2xl font-bold ${isReviewed ? 'text-gray-400' : 'text-green-600'}`}>
                {formatCurrency(rec.impact, 'full')}
              </div>
            </div>

            {/* Discussion button — always visible so comment count is always scannable */}
            <button
              type="button"
              className="relative flex items-center justify-center h-8 w-8 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
              onClick={e => { e.stopPropagation(); onDiscuss(); }}
              title="Discussion"
            >
              <MessageSquare className="h-4 w-4" />
              {commentCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center leading-none">
                  {commentCount > 9 ? '9+' : commentCount}
                </span>
              )}
            </button>

            {isExpanded
              ? <ChevronUp className="h-5 w-5 text-gray-400" />
              : <ChevronDown className="h-5 w-5 text-gray-400" />
            }
          </div>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div
            className="mt-4 pt-4 border-t border-gray-200 space-y-5"
            onClick={e => e.stopPropagation()}
          >
            {/* Why AI surfaced this */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-semibold text-purple-900">Why this was surfaced</span>
              </div>
              <p className="text-sm text-purple-800">{rec.reasoning}</p>
            </div>

            {/* Invoices table */}
            {rec.invoices && rec.invoices.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Related invoices</h4>
                <div className="bg-gray-50 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-gray-700">Invoice</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-700">Customer</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-700">Amount</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-700">
                          {rec.invoices.some(i => i.daysOverdue) ? 'Days Overdue' : 'Due Date'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {rec.invoices.map((inv, idx) => (
                        <tr key={idx} className="hover:bg-white transition-colors">
                          <td className="p-3 text-sm font-medium text-gray-900">{inv.invoiceNumber}</td>
                          <td className="p-3 text-sm text-gray-700">{inv.customerName}</td>
                          <td className="p-3 text-sm text-right font-semibold text-gray-900">{formatCurrency(inv.amount, 'full')}</td>
                          <td className="p-3 text-sm text-right">
                            {inv.daysOverdue
                              ? <Badge variant="destructive">{inv.daysOverdue} days</Badge>
                              : <span className="text-gray-600">{inv.dueDate}</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Bills table */}
            {rec.bills && rec.bills.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Related payments</h4>
                <div className="bg-gray-50 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-gray-700">Supplier</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-700">Amount</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-700">Current due</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-700">Proposed</th>
                        <th className="text-right p-3 text-sm font-medium text-gray-700">Benefit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {rec.bills.map((bill, idx) => (
                        <tr key={idx} className="hover:bg-white transition-colors">
                          <td className="p-3 text-sm font-medium text-gray-900">{bill.supplierName}</td>
                          <td className="p-3 text-sm text-right font-semibold text-gray-900">{formatCurrency(bill.amount, 'full')}</td>
                          <td className="p-3 text-sm text-right text-gray-600">{bill.currentDueDate}</td>
                          <td className="p-3 text-sm text-right text-green-700 font-medium">{bill.proposedDueDate}</td>
                          <td className="p-3 text-sm text-right">
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              {bill.timingBenefit > 0 ? `+${bill.timingBenefit}` : bill.timingBenefit} days
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Suggested actions */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Suggested actions</h4>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <ul className="space-y-2">
                  {rec.suggestedActions.map((action, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-blue-900">
                      <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 pt-2 flex-wrap">
              {/* Open tab buttons */}
              {!isReviewed && (
                <>
                  {/* Generate Recurring Transaction — only on cards with a suggestion */}
                  {rec.suggestRecurringTransaction && canManageRecurring && (
                    <PermissionGate permission="manage_recurring_transactions">
                      <Button
                        className="gap-2 bg-purple-600 hover:bg-purple-700"
                        onClick={onGenerateRecurring}
                      >
                        <RepeatIcon className="h-4 w-4" />
                        Generate Recurring Transaction
                      </Button>
                    </PermissionGate>
                  )}
                  {canCreateAction && (
                    <PermissionGate permission="create_action_from_recommendation">
                      <Button
                        className="gap-2 bg-green-600 hover:bg-green-700"
                        onClick={onCreateAction}
                      >
                        <PlusCircle className="h-4 w-4" />
                        Create Action
                      </Button>
                    </PermissionGate>
                  )}
                  {canDismiss && (
                    <PermissionGate permission="dismiss_recommendation">
                      <Button
                        variant="outline"
                        className="gap-2"
                        onClick={onMarkReviewed}
                      >
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        Mark as Reviewed
                      </Button>
                    </PermissionGate>
                  )}
                </>
              )}

              {/* Reviewed tab — reinstate button */}
              {isReviewed && canDismiss && (
                <PermissionGate permission="dismiss_recommendation">
                  <Button
                    variant="outline"
                    className="gap-2 text-amber-700 border-amber-300 hover:bg-amber-50"
                    onClick={onReinstate}
                  >
                    <RotateCcw className="h-4 w-4" />
                    Reinstate to Open
                  </Button>
                </PermissionGate>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

export const AIRecommendationsPage: React.FC = () => {
  const { formatCurrency } = useFormatting();
  const { hasPermission } = usePermissions();
  const { createAction } = useActions();
  const { mode, currentUser } = useApp();
  const {
    reviewedItems,
    markReviewed,
    reinstate,
    getInsightCommentCount,
  } = useRecommendations();

  const [activeTab, setActiveTab]           = useState<'open' | 'reviewed'>('open');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('ALL');
  const [expandedCards, setExpandedCards]   = useState<Set<string>>(new Set());
  const [showImpactModal, setShowImpactModal]   = useState(false);
  const [createActionRec, setCreateActionRec]   = useState<Recommendation | null>(null);
  const [discussingRec, setDiscussingRec]       = useState<Recommendation | null>(null);
  const [generateRecurringRec, setGenerateRecurringRec] = useState<Recommendation | null>(null);
  const [generatedEntries, setGeneratedEntries] = useState<RecurringTransaction[]>([]);

  // ── Disclosure panel persistence ──────────────────────────────────────────
  // Demo mode: sessionStorage — resets on hard refresh (so every demo starts open)
  // Live mode: localStorage keyed by userId — persists across sessions per user
  const DISCLOSURE_KEY = mode === 'live'
    ? `ai-disclosure-collapsed-${currentUser.id}`
    : 'ai-disclosure-collapsed-demo';

  const [disclosureOpen, setDisclosureOpen] = useState<boolean>(() => {
    if (mode === 'live') {
      return localStorage.getItem(DISCLOSURE_KEY) !== 'true';
    }
    // Demo: default open, but respect if user already collapsed in this session
    return sessionStorage.getItem(DISCLOSURE_KEY) !== 'true';
  });

  useEffect(() => {
    if (mode === 'live') {
      localStorage.setItem(DISCLOSURE_KEY, disclosureOpen ? 'false' : 'true');
    } else {
      sessionStorage.setItem(DISCLOSURE_KEY, disclosureOpen ? 'false' : 'true');
    }
  }, [disclosureOpen, DISCLOSURE_KEY, mode]);

  const canViewOperational  = hasPermission('view_operational_insights');
  const canViewStrategic    = hasPermission('view_strategic_insights');
  const canCreateAction     = hasPermission('create_action_from_recommendation');
  const canDismiss          = hasPermission('dismiss_recommendation');
  const canManageRecurring  = hasPermission('manage_recurring_transactions');

  const handleGenerateRecurring = (entry: RecurringTransaction) => {
    setGeneratedEntries(prev => [...prev, entry]);
    setGenerateRecurringRec(null);
    // Mark the recommendation as reviewed after generating
    if (generateRecurringRec) {
      markReviewed(generateRecurringRec.id);
    }
  };

  const reviewedIds = useMemo(() => new Set(reviewedItems.map(r => r.id)), [reviewedItems]);

  // All recommendations this user can see (no reviewed filter yet)
  const permittedRecommendations = useMemo(() => {
    return allRecommendations.filter(rec => {
      if (rec.category === 'OPERATIONAL' && !canViewOperational) return false;
      if (rec.category === 'STRATEGIC'   && !canViewStrategic)   return false;
      return true;
    });
  }, [canViewOperational, canViewStrategic]);

  // Open tab — not reviewed, then apply UI filters
  const openRecommendations = useMemo(() => {
    return permittedRecommendations.filter(rec => {
      if (reviewedIds.has(rec.id)) return false;
      if (priorityFilter !== 'ALL' && rec.priority !== priorityFilter) return false;
      if (categoryFilter !== 'ALL' && rec.category !== categoryFilter) return false;
      return true;
    });
  }, [permittedRecommendations, reviewedIds, priorityFilter, categoryFilter]);

  // Reviewed tab — reviewed only, most recent first
  const reviewedRecommendations = useMemo(() => {
    return permittedRecommendations
      .filter(rec => reviewedIds.has(rec.id))
      .sort((a, b) => {
        const aTime = reviewedItems.find(r => r.id === a.id)?.reviewedAt.getTime() ?? 0;
        const bTime = reviewedItems.find(r => r.id === b.id)?.reviewedAt.getTime() ?? 0;
        return bTime - aTime; // most recent first
      });
  }, [permittedRecommendations, reviewedIds, reviewedItems]);

  const highPriorityCount = openRecommendations.filter(r => r.priority === 'HIGH').length;

  const toggleCard = (id: string) => {
    const next = new Set(expandedCards);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpandedCards(next);
  };

  const handleMarkReviewed = (id: string) => {
    markReviewed(id);
    // Collapse the card when reviewed
    setExpandedCards(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleReinstate = (id: string) => {
    reinstate(id);
    // Switch back to open tab so user can see the reinstated item
    setActiveTab('open');
  };

  const handleCreateAction = (payload: import('@/types/actions').CreateActionPayload) => {
    createAction(payload);
    setCreateActionRec(null);
  };

  return (
    <div className="space-y-6 p-6">

      {/* Header */}
      <div className="flex justify-end">
        <Button onClick={() => setShowImpactModal(true)} size="lg" className="gap-2 bg-purple-600 hover:bg-purple-700">
          <BarChart3 className="h-5 w-5" />
          View Impact Analysis
        </Button>
      </div>

      {/* ── About these recommendations — collapsible disclosure ── */}
      <Collapsible open={disclosureOpen} onOpenChange={setDisclosureOpen}>
        <Card className="border border-blue-200 bg-blue-50/50">
          <CardContent className="py-4 px-5">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="w-full flex items-center justify-between text-left group"
              >
                <div className="flex items-center gap-2">
                  <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span className="text-sm font-semibold text-blue-900">About these recommendations</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-blue-600">
                  {disclosureOpen ? (
                    <>hide <ChevronUp className="h-3.5 w-3.5" /></>
                  ) : (
                    <>show <ChevronDown className="h-3.5 w-3.5" /></>
                  )}
                </div>
              </button>
            </CollapsibleTrigger>

            <CollapsibleContent>
              <div className="mt-3 space-y-3">
                <p className="text-sm text-blue-800">
                  Recommendations are generated from three sources — all within your own data:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-blue-800">
                    <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Your patterns</strong> — trends, anomalies and timing opportunities identified within your own transactions and forecasts</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-blue-800">
                    <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Your benchmarks</strong> — rules and thresholds configured for your organisation by your system administrator</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-blue-800">
                    <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Financial best practice</strong> — built-in rules based on standard financial principles (payment term norms, working capital optimisation, early payment discount maths)</span>
                  </li>
                </ul>
                <div className="flex items-start gap-2 pt-1 border-t border-blue-200">
                  <Lock className="h-4 w-4 text-blue-700 mt-0.5 flex-shrink-0" />
                  <p className="text-sm font-semibold text-blue-900">
                    Your data never leaves your organisation. No data is shared externally or compared against other organisations to generate these insights.
                  </p>
                </div>
              </div>
            </CollapsibleContent>
          </CardContent>
        </Card>
      </Collapsible>

      {/* Cash position alert bar */}
      <Card className="border-l-4 border-l-amber-500 bg-gradient-to-r from-amber-50 to-orange-50">
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <h3 className="font-semibold text-gray-900">Cash Trend</h3>
            </div>
            <Button onClick={() => setShowImpactModal(true)} size="sm" className="bg-green-600 hover:bg-green-700 h-8 text-xs">
              View Impact
            </Button>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm mb-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white text-xs font-bold">NOW</div>
                <div>
                  <div className="text-[10px] text-gray-500 font-medium">TODAY</div>
                  <div className="text-lg font-bold text-gray-900">{formatCurrency(aiAlertSummary.startingCash, 'full')}</div>
                  <div className="text-[10px] text-green-600 font-medium">Healthy</div>
                </div>
              </div>
              <TrendingDown className="h-5 w-5 text-amber-500" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white text-xs font-bold">90D</div>
                <div>
                  <div className="text-[10px] text-gray-500 font-medium">90 DAYS</div>
                  <div className="text-lg font-bold text-gray-900">{formatCurrency(aiAlertSummary.projectedCash90Days, 'full')}</div>
                  <div className="text-[10px] text-amber-600 font-medium">Needs management</div>
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-600">{aiAlertSummary.message}</p>
        </CardContent>
      </Card>

      {/* Summary metrics — based on open insights only */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-gray-600">Total Potential Impact</div>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {formatCurrency(openRecommendations.reduce((sum, r) => sum + r.impact, 0), 'full')}
            </div>
            <div className="text-sm text-gray-600 mt-1">From {openRecommendations.length} open insights</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-gray-600">High Priority</div>
              <ShieldAlert className="h-4 w-4 text-red-600" />
            </div>
            <div className="text-3xl font-bold text-red-600">{highPriorityCount}</div>
            <div className="text-sm text-gray-600 mt-1">Require immediate attention</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-gray-600">Strategic Insights</div>
              <Brain className="h-4 w-4 text-purple-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {openRecommendations.filter(r => r.category === 'STRATEGIC').length}
            </div>
            <div className="text-sm text-gray-600 mt-1">Pattern & risk analysis</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-gray-600">Reviewed</div>
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            </div>
            <div className="text-3xl font-bold text-green-600">{reviewedItems.length}</div>
            <div className="text-sm text-gray-600 mt-1">
              {reviewedItems.length === 0
                ? 'None reviewed yet'
                : reviewedItems.length === 1 ? '1 insight actioned' : `${reviewedItems.length} insights actioned`}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs — Open / Reviewed */}
      <Tabs value={activeTab} onValueChange={v => setActiveTab(v as 'open' | 'reviewed')}>
        <TabsList className="mb-2">
          <TabsTrigger value="open" className="gap-2">
            Open Insights
            {openRecommendations.length > 0 && (
              <Badge className="bg-blue-600 text-white text-xs px-1.5 py-0 h-5 ml-1">
                {openRecommendations.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="reviewed" className="gap-2">
            Reviewed
            {reviewedRecommendations.length > 0 && (
              <Badge className="bg-green-600 text-white text-xs px-1.5 py-0 h-5 ml-1">
                {reviewedRecommendations.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Open tab ── */}
        <TabsContent value="open" className="space-y-4 mt-4">

          {/* Filters — only shown on open tab */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Filter:</span>
            </div>
            <div className="flex gap-2">
              <Button variant={categoryFilter === 'ALL' ? 'default' : 'outline'} size="sm" onClick={() => setCategoryFilter('ALL')}>
                All
              </Button>
              {canViewStrategic && (
                <Button
                  variant={categoryFilter === 'STRATEGIC' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCategoryFilter('STRATEGIC')}
                  className={categoryFilter === 'STRATEGIC' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                >
                  <Brain className="h-3 w-3 mr-1" /> Strategic
                </Button>
              )}
              {canViewOperational && (
                <Button
                  variant={categoryFilter === 'OPERATIONAL' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCategoryFilter('OPERATIONAL')}
                  className={categoryFilter === 'OPERATIONAL' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                >
                  <Zap className="h-3 w-3 mr-1" /> Operational
                </Button>
              )}
            </div>
            <div className="h-6 w-px bg-gray-300" />
            <div className="flex gap-2">
              {(['ALL', 'HIGH', 'MEDIUM', 'LOW'] as PriorityFilter[]).map(p => (
                <Button
                  key={p}
                  variant={priorityFilter === p ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPriorityFilter(p)}
                  className={
                    priorityFilter === p && p === 'HIGH'   ? 'bg-red-600 hover:bg-red-700' :
                    priorityFilter === p && p === 'MEDIUM' ? 'bg-amber-600 hover:bg-amber-700' :
                    priorityFilter === p && p === 'LOW'    ? 'bg-blue-600 hover:bg-blue-700' : ''
                  }
                >
                  {p === 'ALL' ? 'All Priorities' : p.charAt(0) + p.slice(1).toLowerCase()}
                </Button>
              ))}
            </div>
          </div>

          {/* Open list */}
          {openRecommendations.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-lg font-medium text-gray-900">
                  {reviewedItems.length > 0
                    ? 'All insights reviewed — great work!'
                    : 'No insights match your current filters'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {reviewedItems.length > 0
                    ? `${reviewedItems.length} insight${reviewedItems.length === 1 ? '' : 's'} in the Reviewed tab`
                    : 'Try adjusting the filters above or check back later'}
                </p>
                {reviewedItems.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4 gap-2"
                    onClick={() => setActiveTab('reviewed')}
                  >
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    View Reviewed
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            openRecommendations.map(rec => (
              <RecommendationCard
                key={rec.id}
                rec={rec}
                isExpanded={expandedCards.has(rec.id)}
                onToggle={() => toggleCard(rec.id)}
                onMarkReviewed={() => handleMarkReviewed(rec.id)}
                onCreateAction={() => setCreateActionRec(rec)}
                onGenerateRecurring={rec.suggestRecurringTransaction ? () => setGenerateRecurringRec(rec) : undefined}
                onDiscuss={() => setDiscussingRec(rec)}
                canDismiss={canDismiss}
                canCreateAction={canCreateAction}
                canManageRecurring={canManageRecurring}
                commentCount={getInsightCommentCount(rec.id)}
              />
            ))
          )}
        </TabsContent>

        {/* ── Reviewed tab ── */}
        <TabsContent value="reviewed" className="space-y-4 mt-4">
          {reviewedRecommendations.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-lg font-medium text-gray-900">No reviewed insights yet</p>
                <p className="text-sm text-gray-500 mt-1">
                  When you mark an insight as reviewed it will appear here — the history is never lost
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <p className="text-sm text-gray-500 flex items-center gap-2">
                <RotateCcw className="h-4 w-4" />
                Reviewed insights are kept here permanently. Expand any card and click "Reinstate to Open" if it was marked reviewed in error.
              </p>
              {reviewedRecommendations.map(rec => (
                <RecommendationCard
                  key={rec.id}
                  rec={rec}
                  isExpanded={expandedCards.has(rec.id)}
                  onToggle={() => toggleCard(rec.id)}
                  reviewedItem={reviewedItems.find(r => r.id === rec.id)}
                  onReinstate={() => handleReinstate(rec.id)}
                  onDiscuss={() => setDiscussingRec(rec)}
                  canDismiss={canDismiss}
                  canCreateAction={canCreateAction}
                  canManageRecurring={canManageRecurring}
                  commentCount={getInsightCommentCount(rec.id)}
                />
              ))}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Modals */}
      {generateRecurringRec?.suggestRecurringTransaction && (
        <GenerateRecurringModal
          suggestion={generateRecurringRec.suggestRecurringTransaction}
          onConfirm={handleGenerateRecurring}
          onClose={() => setGenerateRecurringRec(null)}
        />
      )}
      {showImpactModal && <ImpactModal onClose={() => setShowImpactModal(false)} />}

      <InsightDiscussionModal
        rec={discussingRec}
        onClose={() => setDiscussingRec(null)}
      />

      <CreateActionModal
        open={!!createActionRec}
        onClose={() => setCreateActionRec(null)}
        onConfirm={handleCreateAction}
        variant="insight"
        prefill={createActionRec ? {
          title: createActionRec.title,
          description: createActionRec.reasoning,
        } : undefined}
      />
    </div>
  );
};
